import { Component, Input, OnInit } from '@angular/core';
import { SuppliersService } from '../../../../../../suppliers/suppliers.service';
import { MatDialog } from '@angular/material/dialog';
import { isNilty } from '@core/utils.service';
import { RuleSupplierSource, RuleSourceSkuType, RuleOrderType } from '@models/configuration/rules/publication-rule-source-model';
import { Supplier } from '@models/supplier-model';
import { GenericConfirmationModalComponent } from '@shared/generic-confirmation-modal/generic-confirmation-modal.component';
import { Router } from '@angular/router';
import { MarginParameters } from '@models/configuration/rules/pricing-strategy-model';
import { PublicationRulesService } from '../../../publication-rules.service';
import { PublicationSelection } from '@models/configuration/rules/publication-selection-model';
import { SelectionSelectModalComponent } from '../../selection-select-modal/selection-select-modal.component';

@Component({
  selector: 'app-supplier-sources',
  templateUrl: './supplier-sources.component.html',
  styleUrls: ['./supplier-sources.component.scss'],
})
export class SupplierSourcesComponent implements OnInit {
  @Input() storeId?: number;

  ruleSources: RuleSupplierSource[] = [];
  filteredRuleSources: RuleSupplierSource[] = [];

  filterKey: string;

  suppliers: Supplier[] = [];
  childSuppliers: Supplier[] = [];
  selectionsBySupplierId: Record<number, PublicationSelection[]> = {};

  ruleSourceTypes = Object.keys(RuleSourceSkuType);

  ruleOrderTypes = Object.keys(RuleOrderType);

  isPowerSeller = false;

  constructor(
    private suppliersService: SuppliersService,
    private publicationRulesService: PublicationRulesService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  @Input() set _supplierSources(r: RuleSupplierSource[]) {
    this.powerSeller();
    this.ruleSources = r;
    this.initSources();
  }

  ngOnInit() {
    this.suppliersService.getAllSuppliers().subscribe((r) => {
      this.suppliers = r;

      this.childSuppliers = (r || []).filter((s: any) => !isNilty(s?.parentSupplier) || !isNilty(s?.parentSupplierId));

      this.initSources();
    });
  }
  powerSeller() {
    this.isPowerSeller = this.router.url.includes('power-seller-rules');
  }

  add() {
    const newSource = new RuleSupplierSource();
    this.ruleSources.push(newSource);
    this.filteredRuleSources.push(newSource);
  }

  filterSources(string?: string) {
    const sorted = this.sortedSources();
    if (isNilty(string)) {
      this.filteredRuleSources = sorted;
    } else {
      this.filteredRuleSources = sorted.filter((it) => it.supplierCode.toUpperCase().indexOf(string.toUpperCase()) !== -1);
    }
  }

  selectedSource(event: number, source: RuleSupplierSource) {
    this.assignDescription(source, event);
    this.validate(event, source);
    source.selectionIds = [];
    this.loadSelectionsForSupplier(event);
  }

  removeSource(source: RuleSupplierSource) {
    this.dialog
      .open(GenericConfirmationModalComponent, {
        data: `This will remove the Source for ${source.supplierCode} and all its predicates.`,
      })
      .afterClosed()
      .subscribe((r: boolean) => {
        if (r) {
          const index = this.ruleSources.findIndex((it) => it.supplierCode === source.supplierCode);
          this.ruleSources.splice(index, 1);
          this.filterSources(this.filterKey);
        }
      });
  }

  addCustomPricing(source: RuleSupplierSource) {
    source.pricingParameters = new MarginParameters();
  }
  removeCustomPricing(source: RuleSupplierSource) {
    source.pricingParameters = undefined;
  }

  selectedChildSupplier(event: number | null, source: RuleSupplierSource) {
    source.childSupplierId = event ?? undefined;
    if (!isNilty(event)) {
      this.assignChildDescription(source, event);
    } else {
      source.childSupplierCode = undefined;
    }
  }

  selectionCount(source: RuleSupplierSource): number {
    return source.selectionIds?.length || 0;
  }

  openSelectionsModal(source: RuleSupplierSource) {
    const selections = this.selectionsBySupplierId[source.supplierId] || [];
    this.dialog
      .open(SelectionSelectModalComponent, {
        data: {
          selections,
          selectedIds: source.selectionIds || [],
        },
        width: '640px',
      })
      .afterClosed()
      .subscribe((selectedIds: number[] | null) => {
        if (selectedIds != null) {
          source.selectionIds = selectedIds;
        }
      });
  }

  private initSources() {
    if (isNilty(this.ruleSources || isNilty(this.suppliers))) {
      return;
    }
    this.ruleSources.forEach((it) => {
      if (!isNilty(it.supplierId)) {
        this.assignDescription(it, it.supplierId);
        this.loadSelectionsForSupplier(it.supplierId);
      }
      if (!isNilty(it.childSupplierId)) {
        this.assignChildDescription(it, it.childSupplierId);
      }
    });
    this.filterSources();
  }

  private assignChildDescription(source: RuleSupplierSource, supplierId: number) {
    const s = this.childSuppliers.find((it) => it.id === supplierId) || this.suppliers.find((it) => it.id === supplierId);
    if (!isNilty(s)) {
      source.childSupplierId = s.id;
      source.childSupplierCode = s.code;
    }
  }

  private assignDescription(source: RuleSupplierSource, supplierId: number) {
    const s = this.suppliers.find((it) => it.id === supplierId);
    if (!isNilty(s)) {
      source.supplierCode = s.code;
    }
  }

  private sortedSources(): RuleSupplierSource[] {
    return [...this.ruleSources].sort((i, j) => (i.supplierCode > j.supplierCode ? 1 : -1));
  }

  private validate(newId: number, newSource: RuleSupplierSource) {
    if (this.ruleSources.find((s) => s.supplierId === newId)) {
      newSource.valid = false;
      newSource.error = `There is already a source with this supplier`;
    } else {
      newSource.valid = true;
      newSource.error = undefined;
    }
  }

  private loadSelectionsForSupplier(supplierId: number) {
    if (isNilty(supplierId) || isNilty(this.storeId) || this.selectionsBySupplierId[supplierId]) {
      return;
    }
    this.publicationRulesService.findPublicationSelections(supplierId, this.storeId, true).subscribe((selections) => {
      this.selectionsBySupplierId[supplierId] = selections;
    });
  }
}
